import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getSession, 
  getLatestScreenshots, 
  getImageUrl,
  uploadScreenshot,
  updateScreenshotMetadata
} from '../../services/api';
import socketService from '../../services/socket';
import { 
  compressImage, 
  getCompressionStats, 
  isCompressionSupported,
  formatFileSize,
  getCompressionProfile
} from '../../utils/imageCompression';
import {
  shouldShowCameraButton,
  captureFromCamera,
  fileToDataURL
} from '../../utils/cameraUtils';
import { shouldUseMobileUI } from '../../utils/deviceDetection';
import { useSettings } from '../../contexts/SettingsContext';
import MobileNav from '../Mobile/MobileNav';
import FloatingActionButton from '../Mobile/FloatingActionButton';
import './FPSView.css';

function FPSView({ user }) {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [session, setSession] = useState(null);
  const [screenshots, setScreenshots] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [compressionEnabled] = useState(isCompressionSupported());
  const [showCameraButton] = useState(shouldShowCameraButton());
  
  // Mobile UI state
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isMobile] = useState(shouldUseMobileUI());
  
  // Layout and auto-rotate state
  const [layoutMode, setLayoutMode] = useState(() => {
    return localStorage.getItem('fps-layout-mode') || 'right';
  });
  const [autoRotate, setAutoRotate] = useState(() => {
    const saved = localStorage.getItem('fps-auto-rotate');
    return saved !== null ? saved === 'true' : true; // Default to true
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [sessionData, screenshotsData] = await Promise.all([
        getSession(sessionId),
        getLatestScreenshots(sessionId),
      ]);

      setSession(sessionData);
      const newScreenshots = screenshotsData.screenshots || [];
      
      // Sort screenshots by most recent first (descending)
      const sortedScreenshots = [...newScreenshots].sort((a, b) => {
        return new Date(b.capturedAt) - new Date(a.capturedAt);
      });
      
      setScreenshots(sortedScreenshots);
      
      // Keep current selection valid
      if (selectedIndex >= sortedScreenshots.length) {
        setSelectedIndex(0);
      }
      
      setError('');
    } catch (err) {
      console.error('Failed to load FPS view data:', err);
      setError('Failed to load screenshots. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [sessionId, selectedIndex]);

  // Screenshot/Camera capture handler for Electron
  const handleScreenshotCapture = useCallback(async (captureData) => {
    
    try {
      console.log('[FPSView] Screenshot captured:', captureData);
      setUploadProgress(10);
      
      // Read the file using Electron API
      const buffer = await window.electronAPI.readFile(captureData.filepath);
      const blob = new Blob([buffer], { type: 'image/png' });
      
      setUploadProgress(20);
      
      let fileToUpload;
      let filename = captureData.filename;
      let profileUsed = 'none';
      
      // Try to compress image if supported
      if (compressionEnabled) {
        try {
          const profileId = settings.compression.activeProfile;
          const customSettings = profileId === 'custom' ? settings.compression.customProfile : {};
          const profile = getCompressionProfile(profileId, customSettings);
          
          profileUsed = profile.name || profileId;
          
          const compressedBlob = await compressImage(blob, {
            quality: profile.quality,
            maxWidth: profile.maxWidth,
            maxHeight: profile.maxHeight,
          });
          
          filename = captureData.filename.replace(/\.png$/i, '.jpg');
          fileToUpload = new File([compressedBlob], filename, { type: 'image/jpeg' });
          
          setUploadProgress(40);
        } catch (compressionError) {
          console.warn('[FPSView] Compression failed, using original:', compressionError);
          profileUsed = 'failed-fallback-to-png';
          fileToUpload = new File([blob], captureData.filename, { type: 'image/png' });
          setUploadProgress(40);
        }
      } else {
        fileToUpload = new File([blob], captureData.filename, { type: 'image/png' });
        setUploadProgress(40);
      }
      
      setUploadProgress(60);
      
      // Upload
      const uploadStartTime = performance.now();
      const result = await uploadScreenshot(fileToUpload, sessionId);
      const uploadTime = Math.round(performance.now() - uploadStartTime);
      
      setUploadProgress(80);
      
      // Update metadata with upload time and profile
      await updateScreenshotMetadata(result.id, {
        uploadTimeMs: uploadTime,
        metadata: { compressionProfile: profileUsed }
      });
      
      setUploadProgress(100);
      
      // Reload screenshots
      await loadData();
      
      // Reset progress after a short delay
      setTimeout(() => setUploadProgress(0), 1000);
      
    } catch (err) {
      console.error('[FPSView] Screenshot upload failed:', err);
      setError('Failed to upload screenshot');
      setUploadProgress(0);
    }
  }, [sessionId, compressionEnabled, settings.compression.activeProfile, settings.compression.customProfile, loadData]);

  // Camera capture handler for mobile
  const handleCameraCapture = useCallback(async (cameraFile) => {
    try {
      console.log('[FPSView] Camera capture initiated');
      setUploadProgress(5);
      
      // cameraFile is passed as parameter from FloatingActionButton
      if (!cameraFile) {
        throw new Error('No image captured from camera');
      }
      
      setUploadProgress(15);
      
      const blob = new Blob([cameraFile], { type: cameraFile.type });
      let fileToUpload;
      let filename = `camera-${Date.now()}.jpg`;
      let profileUsed = 'none';
      
      // Try to compress image if supported
      if (compressionEnabled) {
        try {
          const profileId = settings.compression.activeProfile;
          const customSettings = profileId === 'custom' ? settings.compression.customProfile : {};
          const profile = getCompressionProfile(profileId, customSettings);
          
          profileUsed = profile.name || profileId;
          
          const compressedBlob = await compressImage(blob, {
            quality: profile.quality,
            maxWidth: profile.maxWidth,
            maxHeight: profile.maxHeight,
          });
          
          fileToUpload = new File([compressedBlob], filename, { type: 'image/jpeg' });
          
          setUploadProgress(40);
        } catch (compressionError) {
          console.warn('[FPSView] Compression failed, using original:', compressionError);
          profileUsed = 'failed-fallback-to-original';
          fileToUpload = new File([blob], filename, { type: cameraFile.type });
          setUploadProgress(40);
        }
      } else {
        fileToUpload = new File([blob], filename, { type: cameraFile.type });
        setUploadProgress(40);
      }
      
      setUploadProgress(60);
      
      // Upload
      const uploadStartTime = performance.now();
      const result = await uploadScreenshot(fileToUpload, sessionId);
      const uploadTime = Math.round(performance.now() - uploadStartTime);
      
      setUploadProgress(80);
      
      // Update metadata with upload time and profile
      await updateScreenshotMetadata(result.id, {
        uploadTimeMs: uploadTime,
        metadata: { compressionProfile: profileUsed }
      });
      
      setUploadProgress(100);
      
      // Reload screenshots
      await loadData();
      
      // Reset progress after a short delay
      setTimeout(() => setUploadProgress(0), 1000);
      
    } catch (err) {
      console.error('[FPSView] Camera upload failed:', err);
      setError('Failed to upload photo');
      setUploadProgress(0);
    }
  }, [sessionId, compressionEnabled, settings.compression.activeProfile, settings.compression.customProfile, loadData]);

  // Load initial data
  useEffect(() => {
    loadData();

    // Join session room for real-time updates
    if (socketService.isConnected()) {
      socketService.joinSession(sessionId);
    }

    // Listen for new screenshots
    const handleScreenshotUploaded = (data) => {
      console.log('[FPSView] New screenshot uploaded:', data);
      loadData(); // Refresh latest screenshots
    };

    socketService.onScreenshotUploaded(handleScreenshotUploaded);

    // Listen for Electron screenshot captures
    let cleanupScreenshotListener;
    if (window.electronAPI) {
      cleanupScreenshotListener = window.electronAPI.onScreenshotCaptured(async (data) => {
        console.log('[FPSView] Screenshot captured:', data);
        await handleScreenshotCapture(data);
      });
    }

    return () => {
      socketService.off('screenshot-uploaded', handleScreenshotUploaded);
      // Cleanup Electron event listener
      if (cleanupScreenshotListener) {
        cleanupScreenshotListener();
      }
    };
  }, [sessionId, loadData, handleScreenshotCapture]);

  // Auto-rotate effect
  useEffect(() => {
    if (!autoRotate) return;

    const interval = setInterval(async () => {
      console.log('[FPSView] Auto-rotate: Fetching latest screenshots...');
      try {
        const screenshotsData = await getLatestScreenshots(sessionId);
        const newScreenshots = screenshotsData.screenshots || [];
        
        if (newScreenshots.length > 0) {
          // Sort screenshots by most recent first (descending)
          const sortedScreenshots = [...newScreenshots].sort((a, b) => {
            return new Date(b.capturedAt) - new Date(a.capturedAt);
          });
          
          // The newest screenshot is now at index 0
          setScreenshots(sortedScreenshots);
          setSelectedIndex(0);
          console.log('[FPSView] Auto-rotate: Selected newest screenshot', {
            index: 0,
            user: sortedScreenshots[0].username,
            timestamp: new Date(sortedScreenshots[0].capturedAt)
          });
        }
      } catch (err) {
        console.error('[FPSView] Auto-rotate fetch failed:', err);
      }
    }, 5000); // 5 seconds

    return () => clearInterval(interval);
  }, [autoRotate, sessionId]);

  // Save layout preference to localStorage
  useEffect(() => {
    localStorage.setItem('fps-layout-mode', layoutMode);
  }, [layoutMode]);

  // Save auto-rotate preference to localStorage
  useEffect(() => {
    localStorage.setItem('fps-auto-rotate', autoRotate.toString());
  }, [autoRotate]);

  const handleThumbnailClick = (index) => {
    // Clicking a thumbnail in auto-rotate mode should pause it
    if (autoRotate) {
      setAutoRotate(false);
    }
    setSelectedIndex(index);
  };

  const toggleAutoRotate = () => {
    setAutoRotate(!autoRotate);
  };

  const handleLayoutChange = (newLayout) => {
    setLayoutMode(newLayout);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    
    return date.toLocaleString();
  };

  // Render thumbnails component
  const renderThumbnails = () => (
    <div className="fps-thumbnails-section">
      <h3 className="thumbnails-header">Squad Members ({screenshots.length})</h3>
      <div className="fps-thumbnails-container">
        {screenshots.map((screenshot, index) => (
          <div
            key={screenshot.id}
            className={`fps-thumbnail ${index === selectedIndex ? 'active' : ''}`}
            onClick={() => handleThumbnailClick(index)}
          >
            <div className="thumbnail-image-wrapper">
              <img
                src={getImageUrl(screenshot.filePath)}
                alt={`${screenshot.displayName || screenshot.username}'s view`}
                className="thumbnail-image"
                onError={(e) => {
                  console.error('Failed to load thumbnail:', screenshot.filePath);
                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="150"%3E%3Crect fill="%231a2332" width="200" height="150"/%3E%3Ctext fill="%23e1e8ed" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle"%3EFailed%3C/text%3E%3C/svg%3E';
                }}
              />
              {index === selectedIndex && (
                <div className="active-indicator">
                  <span>✓</span>
                </div>
              )}
            </div>
            <div className="thumbnail-info">
              <div className="thumbnail-name">{screenshot.displayName || screenshot.username}</div>
              <div className="thumbnail-time">{formatTimestamp(screenshot.capturedAt)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render main image component
  const renderMainImage = () => {
    const selectedScreenshot = screenshots[selectedIndex];
    
    return (
      <div className="fps-main-display">
        <div className="main-image-container">
          <img
            src={getImageUrl(selectedScreenshot.filePath)}
            alt={`Screenshot by ${selectedScreenshot.displayName || selectedScreenshot.username}`}
            className="main-image"
            onError={(e) => {
              console.error('Failed to load main image:', selectedScreenshot.filePath);
              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600"%3E%3Crect fill="%231a2332" width="800" height="600"/%3E%3Ctext fill="%23e1e8ed" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle"%3EImage failed to load%3C/text%3E%3C/svg%3E';
            }}
          />
        </div>
        <div className="main-image-info">
          <div className="player-info">
            <div className="player-avatar">{(selectedScreenshot.displayName || selectedScreenshot.username).charAt(0).toUpperCase()}</div>
            <div className="player-details">
              <h3>{selectedScreenshot.displayName || selectedScreenshot.username}</h3>
              <p className="timestamp">{formatTimestamp(selectedScreenshot.capturedAt)}</p>
            </div>
          </div>
          <div className="screenshot-stats">
            {autoRotate && (
              <span className="stat-badge auto-badge">🔄 Auto-rotating</span>
            )}
            {selectedScreenshot.uploadTimeMs && (
              <span className="stat-badge">⚡ {selectedScreenshot.uploadTimeMs}ms</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="fps-view loading">
        <div className="loading-spinner"></div>
        <p>Loading FPS view...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fps-view error">
        <div className="error-message">
          <h2>⚠️ Error</h2>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={loadData}>
            Try Again
          </button>
          <button className="btn btn-secondary" onClick={() => navigate(`/session/${sessionId}`)}>
            Back to Session
          </button>
        </div>
      </div>
    );
  }

  if (screenshots.length === 0) {
    return (
      <div className="fps-view empty">
        <div className="empty-state">
          <h2>📸 No Screenshots Yet</h2>
          <p>Waiting for squad members to capture screenshots...</p>
          <button className="btn btn-secondary" onClick={() => navigate(`/session/${sessionId}`)}>
            Back to Session
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fps-view">
      {/* Mobile Navigation */}
      {isMobile && (
        <MobileNav
          isOpen={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          user={user}
          onLogout={() => navigate('/')}
        />
      )}

      <header className="fps-header">
        <button className="btn btn-secondary" onClick={() => navigate(`/session/${sessionId}`)}>
          ← Back to Session
        </button>
        <div className="fps-title">
          <h1>🎮 FPS View</h1>
          {!isMobile && (
            <p className="text-muted">
              {session?.name || 'Unnamed Session'} • {screenshots.length} player{screenshots.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        <div className="fps-controls">
          {isMobile ? (
            <button 
              className="btn-hamburger" 
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open menu"
            >
              ☰
            </button>
          ) : (
            <>
              {/* Capture button - Desktop/Electron only */}
              {!showCameraButton && (
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    if (window.electronAPI) {
                      await window.electronAPI.captureScreenshot();
                    }
                  }}
                  disabled={uploadProgress > 0}
                  title="Press F9 to capture"
                >
                  📸 Capture
                </button>
              )}
              
              {/* Camera button - Mobile only */}
              {showCameraButton && (
                <button
                  className="btn btn-primary btn-camera"
                  onClick={handleCameraCapture}
                  disabled={uploadProgress > 0}
                  title="Take photo with camera"
                >
                  📷 Take Photo
                </button>
              )}
              
              {/* Layout selector */}
              <div className="layout-selector">
                <button
                  className={`layout-btn ${layoutMode === 'bottom' ? 'active' : ''}`}
                  onClick={() => handleLayoutChange('bottom')}
                  title="Bottom Layout"
                >
                  ⊞
                </button>
                <button
                  className={`layout-btn ${layoutMode === 'right' ? 'active' : ''}`}
                  onClick={() => handleLayoutChange('right')}
                  title="Right Sidebar"
                >
                  ⊟
                </button>
                <button
                  className={`layout-btn ${layoutMode === 'left' ? 'active' : ''}`}
                  onClick={() => handleLayoutChange('left')}
                  title="Left Sidebar"
                >
                  ⊞
                </button>
              </div>
              
              {/* Auto-rotate toggle */}
              <button
                className={`btn ${autoRotate ? 'btn-primary' : 'btn-secondary'}`}
                onClick={toggleAutoRotate}
                title={autoRotate ? 'Stop Auto-Rotate' : 'Start Auto-Rotate (5s)'}
              >
                {autoRotate ? '⏸ Auto' : '▶ Auto'}
              </button>
              
              {/* Refresh button */}
              <button className="btn btn-primary" onClick={loadData} disabled={loading}>
                🔄 Refresh
              </button>
            </>
          )}
        </div>
      </header>

      {/* Upload progress indicator */}
      {uploadProgress > 0 && (
        <div className="upload-progress-container">
          <div className="upload-progress-bar" style={{ width: `${uploadProgress}%` }}>
            <span className="upload-progress-text">{uploadProgress}%</span>
          </div>
        </div>
      )}

      <main className={`fps-content layout-${layoutMode}`}>
        {layoutMode === 'bottom' && (
          <>
            {renderMainImage()}
            {renderThumbnails()}
          </>
        )}
        
        {layoutMode === 'right' && (
          <>
            {renderMainImage()}
            {renderThumbnails()}
          </>
        )}
        
        {layoutMode === 'left' && (
          <>
            {renderThumbnails()}
            {renderMainImage()}
          </>
        )}
      </main>

      {/* Floating Action Button (Mobile Only) */}
      {isMobile && (
        <FloatingActionButton
          visible={true}
          uploadProgress={uploadProgress}
          onCapture={handleCameraCapture}
        />
      )}
    </div>
  );
}

export default FPSView;
